import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { getDataAPI, postDataAPI } from '../lib/api'
import { trackTutorialBegin, trackTutorialComplete, trackCoreActivationCompleted } from '../lib/analytics'
import { logger } from '../lib/logger'
import BrandLogo from './BrandLogo'
import {
  Activity,
  Calendar,
  CheckCircle2,
  ChevronRight,
  FileSpreadsheet,
  Handshake,
  LayoutDashboard,
  Link2,
  ListTodo,
  MapPinned,
  Mic2,
  Music,
  Settings,
  Shield,
  ToggleRight,
  Upload,
  Users,
  Wallet,
  Zap,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

const BRAND = '#FF2300'
const BG = '#0A0A0A'

type TutorialTrack = 'artist' | 'manager' | 'support' | 'team-member'

type Step = {
  tag: string
  title: string
  subtitle: string
  visual: 'welcome' | 'calendar' | 'sheets' | 'shows' | 'team' | 'ready' | 'team-welcome' | 'accounts' | 'approvals' | 'permissions' | 'team-ready'
  highlights: Array<{
    icon: LucideIcon
    label: string
    detail: string
  }>
  showGoogleSheetsCredentials?: boolean
  actions?: Array<{
    label: string
    type: 'primary' | 'secondary'
    onClick: 'openGoogleCalendar' | 'openGoogleSheets' | 'openShows' | 'openMusic' | 'openTeam' | 'openSettings'
  }>
}

const artistSteps: Step[] = [
  {
    tag: 'Getting Started',
    title: 'Welcome to Dizee',
    subtitle: 'Your entire operation — releases, touring, finance, team — lives in one intelligent workspace. Here\'s how to make it yours.',
    visual: 'welcome',
    highlights: [
      { icon: LayoutDashboard, label: 'Dashboard', detail: 'See everything that matters today — upcoming shows, pending tasks, active deals, and recent activity.' },
      { icon: Music, label: 'Music & Releases', detail: 'Track your catalog, streaming data, and release timelines. Import from distributors or add manually.' },
      { icon: Mic2, label: 'Shows & Touring', detail: 'Manage your tour dates, venues, ticket sales, settlements, and travel logistics.' },
    ],
  },
  {
    tag: 'Integrations',
    title: 'Connect Google Calendar',
    subtitle: 'Two-way sync keeps your shows, tasks, and events aligned across Dizee and Google Calendar automatically — no manual updates needed.',
    visual: 'calendar',
    highlights: [
      { icon: Calendar, label: 'Automatic Sync', detail: 'Events you create in either place appear in both. Edits propagate instantly.' },
      { icon: Zap, label: 'All Devices', detail: 'Your schedule stays current on every device — phone, tablet, desktop — without lifting a finger.' },
      { icon: CheckCircle2, label: '30-Second Setup', detail: 'Click below to authorize your Google account. That\'s it — Dizee handles the rest.' },
    ],
    actions: [{ label: 'Connect Google Calendar', type: 'primary', onClick: 'openGoogleCalendar' }],
  },
  {
    tag: 'Integrations',
    title: 'Set Up Google Sheets',
    subtitle: 'We have a step-by-step setup wizard in Settings that walks you through copying the template, connecting your credentials, and enabling sync. It takes about 3 minutes.',
    visual: 'sheets',
    showGoogleSheetsCredentials: false,
    highlights: [
      { icon: FileSpreadsheet, label: 'Guided Setup', detail: 'Open the Sheets Setup Wizard in Settings — it walks you through every step one at a time, no guesswork.' },
      { icon: Settings, label: 'Copy, Paste, Done', detail: 'The wizard gives you your credentials and tells you exactly where to paste each one inside your Google Sheet.' },
      { icon: Zap, label: 'Live Sync', detail: 'Once connected, edits in the Bookings and Catalog tabs flow into Dizee instantly. Your team can work from sheets while you use the app.' },
    ],
    actions: [{ label: 'Open Sheets Setup', type: 'primary', onClick: 'openSettings' }],
  },
  {
    tag: 'Your Data',
    title: 'Import Your Shows',
    subtitle: 'Already have tour dates in a spreadsheet? Upload a CSV and Dizee will parse and import everything — dates, venues, financials, and more.',
    visual: 'shows',
    highlights: [
      { icon: Upload, label: 'CSV Upload', detail: 'Export your existing spreadsheet as a .csv file. Dizee maps columns automatically.' },
      { icon: Mic2, label: 'Review & Import', detail: 'Preview the parsed data before confirming. Fix any mapping issues right in the importer.' },
      { icon: MapPinned, label: 'Touring Dashboard', detail: 'Once imported, your shows appear on the map, calendar, and financial reports immediately.' },
    ],
    actions: [{ label: 'Go to Shows', type: 'primary', onClick: 'openShows' }],
  },
  {
    tag: 'Collaboration',
    title: 'Invite Your Team',
    subtitle: 'Add your manager, agent, publicist, tour manager — anyone who needs access. Each person gets granular, section-level permissions.',
    visual: 'team',
    highlights: [
      { icon: Users, label: 'Invite by Email', detail: 'Send invites to your team. They\'ll create a Dizee account and see your data based on the permissions you set.' },
      { icon: Shield, label: 'Granular Permissions', detail: 'Control access per section — Music, Shows, Money, Tasks, Chat, and more. View-only or full edit.' },
      { icon: ListTodo, label: 'Tasks & Chat', detail: 'Assign tasks with due dates and priorities. Chat in real-time with mentions and notifications.' },
    ],
  },
  {
    tag: 'All Set',
    title: 'You\'re Ready',
    subtitle: 'Your workspace is configured. Here are a few more areas to explore as you settle in.',
    visual: 'ready',
    highlights: [
      { icon: Wallet, label: 'Money', detail: 'Track income from streaming, shows, and merch. Build deal memos and export reports for accounting.' },
      { icon: Handshake, label: 'Deals', detail: 'Manage offers, negotiations, and contracts with a full pipeline view and document storage.' },
      { icon: Link2, label: 'Links', detail: 'Create smart links for pre-saves, release campaigns, and bios. Track clicks and conversions.' },
    ],
  },
]

const teamSteps: Step[] = [
  {
    tag: 'Getting Started',
    title: 'Welcome to Your Team Hub',
    subtitle: 'An artist has added you to their team. You\'ll see their data based on the permissions they\'ve set — and everything stays in sync in real-time.',
    visual: 'team-welcome',
    highlights: [
      { icon: Shield, label: 'Permission-Based', detail: 'Your sidebar only shows sections you have access to. The artist controls what you can view or edit.' },
      { icon: Zap, label: 'Real-Time Sync', detail: 'Any changes you make (within your permissions) appear instantly for the entire team.' },
      { icon: Activity, label: 'Activity Logged', detail: 'Every edit is tracked in the Activity feed so the artist and team can see what changed and when.' },
    ],
  },
  {
    tag: 'Navigation',
    title: 'Managing Your Accounts',
    subtitle: 'If you\'re on multiple artist teams, the Account Switcher in the sidebar lets you toggle between them. Turn accounts on or off to control what you see.',
    visual: 'accounts',
    highlights: [
      { icon: ToggleRight, label: 'Toggle Accounts', detail: 'Switch artist accounts on/off with the toggles. This controls which data appears across all sections.' },
      { icon: LayoutDashboard, label: 'Consolidated View', detail: 'When multiple accounts are active, sections blend data from all of them into one unified view.' },
      { icon: CheckCircle2, label: 'Persistent State', detail: 'Your toggle choices are saved — when you sign back in, you pick up right where you left off.' },
    ],
    actions: [{ label: 'Go to Team Hub', type: 'primary', onClick: 'openTeam' }],
  },
  {
    tag: 'Workflow',
    title: 'Edits & Approvals',
    subtitle: 'Depending on your permissions, your edits may need approval from the artist or another team member before they go live.',
    visual: 'approvals',
    highlights: [
      { icon: Activity, label: 'Activity Tracking', detail: 'Everything you do is logged — the artist and team leads can see what changed, who changed it, and when.' },
      { icon: Shield, label: 'Approval Flow', detail: 'If you don\'t have approval authority, your edits enter a "pending" state until someone with authority approves.' },
      { icon: Zap, label: 'Pending Items', detail: 'While an edit is pending, the item shows an orange indicator and is locked from further changes until resolved.' },
    ],
  },
  {
    tag: 'Access',
    title: 'Understanding Permissions',
    subtitle: 'Your access level is set per section by the artist. Some sections may be view-only, others fully editable, and some may not be visible at all.',
    visual: 'permissions',
    highlights: [
      { icon: Shield, label: 'View vs. Edit', detail: 'Some sections let you only view data. Others give full editing access. Check with the artist to adjust.' },
      { icon: Users, label: 'Team Management', detail: 'If granted, you can invite or manage other team members on the artist\'s behalf from the Team page.' },
      { icon: Wallet, label: 'Financial Access', detail: 'Money and deal data is controlled separately — you\'ll only see it if the artist has explicitly enabled it.' },
    ],
  },
  {
    tag: 'All Set',
    title: 'You\'re Ready to Collaborate',
    subtitle: 'Explore the sections available to you. Use Tasks and Chat to stay aligned with the team.',
    visual: 'team-ready',
    highlights: [
      { icon: ListTodo, label: 'Tasks', detail: 'Create and manage tasks with due dates, priorities, and assignments. Stay on top of deadlines together.' },
      { icon: Mic2, label: 'Your Sections', detail: 'Navigate the sidebar — only the sections you have access to appear. Dig in and start working.' },
      { icon: Activity, label: 'Activity Feed', detail: 'If you have access, use the Activity page to see what\'s changed across the account and track approvals.' },
    ],
  },
]

/* ── Visual mockup components ──────────────────────────────────────────── */

function GlassCard({ children, className = '', glow = false }: { children: React.ReactNode; className?: string; glow?: boolean }) {
  return (
    <div
      className={`rounded-xl border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm ${glow ? 'relative isolate overflow-hidden' : ''} ${className}`}
      style={glow ? { boxShadow: '0 0 28px -10px rgba(255,35,0,0.12)' } : {}}
    >
      {children}
    </div>
  )
}

function MiniNavItem({ icon: Icon, label, color, active = false }: { icon: LucideIcon; label: string; color: string; active?: boolean }) {
  return (
    <div className={`flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[11px] transition-colors ${active ? 'bg-white/[0.06]' : ''}`}>
      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
      <span className={active ? 'text-white font-medium' : 'text-zinc-500'}>{label}</span>
    </div>
  )
}

function VisualWelcome() {
  const navPreview: Array<{ icon: LucideIcon; label: string; color: string }> = [
    { icon: LayoutDashboard, label: 'Dashboard', color: '#818cf8' },
    { icon: Calendar, label: 'Schedule', color: '#f59e0b' },
    { icon: Music, label: 'Music', color: '#1DB954' },
    { icon: Mic2, label: 'Shows', color: '#9333ea' },
    { icon: MapPinned, label: 'Touring', color: '#f97316' },
    { icon: ListTodo, label: 'Tasks', color: '#FF6B35' },
    { icon: Wallet, label: 'Money', color: '#10b981' },
    { icon: Handshake, label: 'Deals', color: '#ea580c' },
    { icon: Link2, label: 'Links', color: '#0ea5e9' },
    { icon: Users, label: 'Team', color: '#8b5cf6' },
    { icon: Settings, label: 'Settings', color: '#64748b' },
  ]
  return (
    <div className="space-y-3">
      <GlassCard className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-3 h-3 rounded-sm" style={{ background: BRAND }} />
          <BrandLogo className="h-2 w-auto text-white" />
        </div>
        <div className="space-y-0.5">
          {navPreview.map((n) => (
            <MiniNavItem key={n.label} icon={n.icon} label={n.label} color={n.color} active={n.label === 'Dashboard'} />
          ))}
        </div>
      </GlassCard>
      <GlassCard glow className="p-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-[#FF2300]/20 flex items-center justify-center">
            <Zap className="w-3 h-3 text-[#FF2300]" />
          </div>
          <div>
            <p className="text-[10px] text-white font-medium">Daily Digest</p>
            <p className="text-[9px] text-zinc-500">AI-powered daily briefing</p>
          </div>
        </div>
      </GlassCard>
    </div>
  )
}

function VisualCalendar() {
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
  const dates = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26]
  const highlightDates = [10, 14, 18, 22]
  const events = [
    { color: 'bg-purple-500', label: 'Show — The Roxy, LA', time: 'Apr 10 · 8pm' },
    { color: 'bg-amber-500', label: 'Rehearsal — Studio B', time: 'Apr 12 · 2pm' },
    { color: 'bg-purple-500', label: 'Show — Bowery Ballroom', time: 'Apr 14 · 9pm' },
    { color: 'bg-sky-500', label: 'Press Interview', time: 'Apr 18 · 11am' },
  ]
  return (
    <div className="space-y-3">
      <GlassCard className="p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] font-semibold text-white uppercase tracking-wider">April 2026</span>
          <Calendar className="w-3.5 h-3.5 text-zinc-500" />
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {days.map((d, i) => (
            <span key={i} className="text-[9px] text-zinc-600 text-center font-medium">{d}</span>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {dates.map((d) => (
            <div
              key={d}
              className={`text-[10px] text-center py-1 rounded-md ${
                highlightDates.includes(d) ? 'bg-[#FF2300]/20 text-[#FF2300] font-semibold' : d === 12 ? 'bg-white/10 text-white font-semibold' : 'text-zinc-500'
              }`}
            >
              {d}
            </div>
          ))}
        </div>
      </GlassCard>
      <GlassCard className="p-4">
        <p className="text-[9px] text-zinc-500 uppercase tracking-wider font-semibold mb-2.5">Upcoming Events</p>
        <div className="space-y-1.5">
          {events.map((ev) => (
            <div key={ev.label} className="flex items-center gap-2 rounded-md bg-white/[0.04] px-2.5 py-2">
              <div className={`w-1 h-5 rounded-full ${ev.color} shrink-0`} />
              <div className="min-w-0">
                <p className="text-[10px] text-zinc-300 truncate">{ev.label}</p>
                <p className="text-[8px] text-zinc-600">{ev.time}</p>
              </div>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  )
}

function VisualSheets() {
  const rows = [
    ['Venue', 'Date', 'City', 'Status'],
    ['The Roxy', '04/14', 'Los Angeles', 'Confirmed'],
    ['Bowery', '04/18', 'New York', 'Hold'],
    ['Metro', '04/22', 'Chicago', 'Pending'],
    ['9:30 Club', '04/26', 'Washington', 'Confirmed'],
    ['The Fillmore', '04/30', 'San Francisco', 'TBD'],
  ]
  return (
    <div className="space-y-3">
      <GlassCard className="p-4 overflow-hidden">
        <div className="flex items-center gap-2 mb-3">
          <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-500" />
          <span className="text-[11px] font-semibold text-white">Google Sheets Template</span>
        </div>
        <div className="rounded-lg border border-white/[0.06] overflow-hidden">
          {rows.map((row, ri) => (
            <div key={ri} className={`grid grid-cols-4 ${ri === 0 ? 'bg-white/[0.06]' : ri % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
              {row.map((cell, ci) => (
                <span
                  key={ci}
                  className={`px-2 py-1.5 text-[9px] border-r border-white/[0.04] last:border-r-0 ${
                    ri === 0 ? 'font-semibold text-zinc-400 uppercase tracking-wider' : 'text-zinc-500'
                  } ${cell === 'Confirmed' ? 'text-emerald-400' : cell === 'Hold' ? 'text-amber-400' : cell === 'Pending' ? 'text-sky-400' : cell === 'TBD' ? 'text-zinc-600' : ''}`}
                >
                  {cell}
                </span>
              ))}
            </div>
          ))}
        </div>
      </GlassCard>
      <GlassCard glow className="p-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-emerald-500/20 flex items-center justify-center">
            <Zap className="w-3 h-3 text-emerald-400" />
          </div>
          <div>
            <p className="text-[10px] text-white font-medium">Live Sync Active</p>
            <p className="text-[9px] text-zinc-500">Sheet edits flow into Dizee instantly</p>
          </div>
        </div>
      </GlassCard>
    </div>
  )
}

function VisualShows() {
  const shows = [
    { venue: 'The Roxy Theatre', city: 'Los Angeles, CA', date: 'Apr 14', status: 'Confirmed', color: 'bg-emerald-500/20 text-emerald-400' },
    { venue: 'Bowery Ballroom', city: 'New York, NY', date: 'Apr 18', status: 'On Sale', color: 'bg-sky-500/20 text-sky-400' },
    { venue: 'Metro Chicago', city: 'Chicago, IL', date: 'Apr 22', status: 'Pending', color: 'bg-amber-500/20 text-amber-400' },
    { venue: '9:30 Club', city: 'Washington, DC', date: 'Apr 26', status: 'Confirmed', color: 'bg-emerald-500/20 text-emerald-400' },
  ]
  return (
    <div className="space-y-3">
      <GlassCard glow className="p-3">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-sm font-semibold text-white">12</p>
            <p className="text-[8px] text-zinc-500 mt-0.5">Total</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-400">8</p>
            <p className="text-[8px] text-zinc-500 mt-0.5">Confirmed</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-amber-400">4</p>
            <p className="text-[8px] text-zinc-500 mt-0.5">Pending</p>
          </div>
        </div>
      </GlassCard>
      <GlassCard className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Mic2 className="w-3.5 h-3.5 text-purple-400" />
          <span className="text-[11px] font-semibold text-white">Upcoming Shows</span>
        </div>
        <div className="space-y-2">
          {shows.map((s) => (
            <div key={s.venue} className="flex items-center justify-between rounded-lg bg-white/[0.04] px-3 py-2.5">
              <div>
                <p className="text-[11px] text-white font-medium">{s.venue}</p>
                <p className="text-[9px] text-zinc-500 mt-0.5">{s.city} · {s.date}</p>
              </div>
              <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded ${s.color}`}>{s.status}</span>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  )
}

function VisualTeam() {
  const members = [
    { name: 'Sarah Chen', role: 'Manager', initials: 'SC', color: 'bg-purple-500/30' },
    { name: 'Mike Torres', role: 'Tour Manager', initials: 'MT', color: 'bg-amber-500/30' },
    { name: 'Jordan Lee', role: 'Publicist', initials: 'JL', color: 'bg-sky-500/30' },
  ]
  const sections = ['Music', 'Shows', 'Money', 'Tasks', 'Chat']
  return (
    <GlassCard className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-3.5 h-3.5 text-purple-400" />
        <span className="text-[11px] font-semibold text-white">Your Team</span>
      </div>
      <div className="space-y-2 mb-3">
        {members.map((m) => (
          <div key={m.name} className="flex items-center gap-2.5 rounded-lg bg-white/[0.04] px-3 py-2">
            <div className={`w-6 h-6 rounded-full ${m.color} flex items-center justify-center`}>
              <span className="text-[8px] font-bold text-white">{m.initials}</span>
            </div>
            <div>
              <p className="text-[10px] text-white font-medium">{m.name}</p>
              <p className="text-[9px] text-zinc-500">{m.role}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-white/[0.06] pt-2.5">
        <p className="text-[9px] text-zinc-500 uppercase tracking-wider font-semibold mb-1.5">Permissions</p>
        <div className="flex flex-wrap gap-1">
          {sections.map((s) => (
            <span key={s} className="text-[8px] text-zinc-400 bg-white/[0.06] rounded px-1.5 py-0.5">{s}</span>
          ))}
        </div>
      </div>
    </GlassCard>
  )
}

function VisualReady() {
  const explore = [
    { icon: Wallet, label: 'Money', desc: 'Revenue & expenses', color: '#10b981' },
    { icon: Handshake, label: 'Deals', desc: 'Offers & contracts', color: '#ea580c' },
    { icon: Link2, label: 'Links', desc: 'Smart links & bios', color: '#0ea5e9' },
    { icon: MapPinned, label: 'Touring', desc: 'Travel & logistics', color: '#f97316' },
  ]
  return (
    <div className="space-y-3">
      <GlassCard glow className="p-4">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div>
            <p className="text-lg font-semibold text-white">12</p>
            <p className="text-[9px] text-zinc-500 mt-0.5">Shows</p>
          </div>
          <div>
            <p className="text-lg font-semibold text-white">34</p>
            <p className="text-[9px] text-zinc-500 mt-0.5">Tracks</p>
          </div>
          <div>
            <p className="text-lg font-semibold text-white">5</p>
            <p className="text-[9px] text-zinc-500 mt-0.5">Team</p>
          </div>
        </div>
      </GlassCard>
      <GlassCard className="p-4">
        <p className="text-[9px] text-zinc-500 uppercase tracking-wider font-semibold mb-2.5">Explore Next</p>
        <div className="space-y-1.5">
          {explore.map((e) => (
            <div key={e.label} className="flex items-center gap-2.5 rounded-lg bg-white/[0.04] px-2.5 py-2">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: `${e.color}20` }}>
                <e.icon className="w-3 h-3" style={{ color: e.color }} />
              </div>
              <div>
                <p className="text-[10px] text-white font-medium">{e.label}</p>
                <p className="text-[8px] text-zinc-600">{e.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  )
}

function VisualTeamWelcome() {
  return (
    <div className="space-y-3">
      <GlassCard className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-3 h-3 rounded-sm" style={{ background: BRAND }} />
          <BrandLogo className="h-2 w-auto text-white" />
        </div>
        <div className="space-y-0.5">
          <MiniNavItem icon={LayoutDashboard} label="Dashboard" color="#818cf8" active />
          <MiniNavItem icon={Calendar} label="Schedule" color="#f59e0b" />
          <MiniNavItem icon={Music} label="Music" color="#1DB954" />
          <MiniNavItem icon={Mic2} label="Shows" color="#9333ea" />
          <MiniNavItem icon={ListTodo} label="Tasks" color="#FF6B35" />
        </div>
        <div className="mt-3 border-t border-white/[0.06] pt-2.5">
          <p className="text-[9px] text-zinc-500 italic">Sections depend on your permissions</p>
        </div>
      </GlassCard>
      <GlassCard className="p-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-emerald-500/20 flex items-center justify-center">
            <Zap className="w-3 h-3 text-emerald-400" />
          </div>
          <div>
            <p className="text-[10px] text-white font-medium">Real-Time Sync</p>
            <p className="text-[9px] text-zinc-500">Changes appear instantly for everyone</p>
          </div>
        </div>
      </GlassCard>
    </div>
  )
}

function VisualAccounts() {
  const artists = [
    { name: 'Luna Martinez', initials: 'LM', active: true },
    { name: 'The Drift', initials: 'TD', active: true },
    { name: 'KODA', initials: 'KO', active: false },
  ]
  return (
    <GlassCard className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <ToggleRight className="w-3.5 h-3.5 text-[#FF2300]" />
        <span className="text-[11px] font-semibold text-white">Account Switcher</span>
      </div>
      <div className="space-y-2">
        {artists.map((a) => (
          <div key={a.name} className="flex items-center justify-between rounded-lg bg-white/[0.04] px-3 py-2.5">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-white/[0.08] flex items-center justify-center">
                <span className="text-[9px] font-bold text-white">{a.initials}</span>
              </div>
              <span className="text-[11px] text-white font-medium">{a.name}</span>
            </div>
            <div className={`w-8 h-4.5 rounded-full relative ${a.active ? 'bg-[#FF2300]' : 'bg-zinc-700'}`}>
              <div className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-all ${a.active ? 'right-0.5' : 'left-0.5'}`} />
            </div>
          </div>
        ))}
      </div>
    </GlassCard>
  )
}

function VisualApprovals() {
  const items = [
    { action: 'Updated show date', who: 'You', time: '2 min ago', status: 'Approved', statusColor: 'text-emerald-400 bg-emerald-500/20' },
    { action: 'Added merch item', who: 'You', time: '15 min ago', status: 'Pending', statusColor: 'text-amber-400 bg-amber-500/20' },
    { action: 'Edited setlist', who: 'Sarah C.', time: '1 hr ago', status: 'Approved', statusColor: 'text-emerald-400 bg-emerald-500/20' },
    { action: 'Changed ticket price', who: 'You', time: '3 hrs ago', status: 'Denied', statusColor: 'text-red-400 bg-red-500/20' },
  ]
  return (
    <div className="space-y-3">
      <GlassCard glow className="p-3">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-sm font-semibold text-emerald-400">12</p>
            <p className="text-[8px] text-zinc-500 mt-0.5">Approved</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-amber-400">3</p>
            <p className="text-[8px] text-zinc-500 mt-0.5">Pending</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-red-400">1</p>
            <p className="text-[8px] text-zinc-500 mt-0.5">Denied</p>
          </div>
        </div>
      </GlassCard>
      <GlassCard className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-3.5 h-3.5 text-sky-400" />
          <span className="text-[11px] font-semibold text-white">Recent Activity</span>
        </div>
        <div className="space-y-1.5">
          {items.map((it) => (
            <div key={it.action} className="flex items-center justify-between rounded-lg bg-white/[0.04] px-3 py-2">
              <div>
                <p className="text-[10px] text-white font-medium">{it.action}</p>
                <p className="text-[9px] text-zinc-600">by {it.who} · {it.time}</p>
              </div>
              <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 ${it.statusColor}`}>{it.status}</span>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  )
}

function VisualPermissions() {
  const perms: Array<{ icon: LucideIcon; section: string; level: string; color: string; iconColor: string }> = [
    { icon: Music, section: 'Music', level: 'Edit', color: 'text-emerald-400', iconColor: '#1DB954' },
    { icon: Mic2, section: 'Shows', level: 'Edit', color: 'text-emerald-400', iconColor: '#9333ea' },
    { icon: Wallet, section: 'Money', level: 'View Only', color: 'text-amber-400', iconColor: '#10b981' },
    { icon: ListTodo, section: 'Tasks', level: 'Edit', color: 'text-emerald-400', iconColor: '#FF6B35' },
    { icon: Handshake, section: 'Deals', level: 'Hidden', color: 'text-zinc-600', iconColor: '#3f3f46' },
    { icon: Users, section: 'Team', level: 'View Only', color: 'text-amber-400', iconColor: '#8b5cf6' },
    { icon: Activity, section: 'Activity', level: 'View Only', color: 'text-amber-400', iconColor: '#6366f1' },
  ]
  return (
    <GlassCard className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-3.5 h-3.5 text-amber-400" />
        <span className="text-[11px] font-semibold text-white">Your Permissions</span>
      </div>
      <div className="space-y-1">
        {perms.map((p) => (
          <div key={p.section} className="flex items-center justify-between py-2 border-b border-white/[0.04] last:border-0">
            <div className="flex items-center gap-2">
              <p.icon className="w-3 h-3 shrink-0" style={{ color: p.iconColor }} />
              <span className="text-[10px] text-zinc-400">{p.section}</span>
            </div>
            <span className={`text-[9px] font-semibold uppercase ${p.color}`}>{p.level}</span>
          </div>
        ))}
      </div>
    </GlassCard>
  )
}

function VisualTeamReady() {
  const tasks = [
    { title: 'Confirm venue deposit', due: 'Due tomorrow', priority: 'text-red-400 bg-red-500/20', tag: 'Urgent' },
    { title: 'Review press kit', due: 'Due Apr 16', priority: 'text-amber-400 bg-amber-500/20', tag: 'Medium' },
    { title: 'Upload rider document', due: 'Due Apr 18', priority: 'text-sky-400 bg-sky-500/20', tag: 'Normal' },
  ]
  return (
    <div className="space-y-3">
      <GlassCard glow className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <ListTodo className="w-3.5 h-3.5 text-[#FF2300]" />
          <span className="text-[11px] font-semibold text-white">Your Tasks</span>
        </div>
        <div className="space-y-1.5">
          {tasks.map((t) => (
            <div key={t.title} className="flex items-center justify-between rounded-lg bg-white/[0.04] px-3 py-2">
              <div>
                <p className="text-[10px] text-white font-medium">{t.title}</p>
                <p className="text-[9px] text-zinc-600">{t.due}</p>
              </div>
              <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 ${t.priority}`}>{t.tag}</span>
            </div>
          ))}
        </div>
      </GlassCard>
      <GlassCard className="p-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <Mic2 className="w-3 h-3 text-purple-400" />
          </div>
          <div>
            <p className="text-[10px] text-white font-medium">2 upcoming shows</p>
            <p className="text-[9px] text-zinc-500">Next: The Roxy — Apr 14</p>
          </div>
        </div>
      </GlassCard>
    </div>
  )
}

const visualComponents: Record<Step['visual'], React.FC> = {
  welcome: VisualWelcome,
  calendar: VisualCalendar,
  sheets: VisualSheets,
  shows: VisualShows,
  team: VisualTeam,
  ready: VisualReady,
  'team-welcome': VisualTeamWelcome,
  accounts: VisualAccounts,
  approvals: VisualApprovals,
  permissions: VisualPermissions,
  'team-ready': VisualTeamReady,
}

/* ── Progress sweep bar (matches digest chrome) ─────────────────────── */

function ProgressBar({ current, total }: { current: number; total: number }) {
  const pct = ((current + 1) / total) * 100
  return (
    <div className="relative h-[3px] w-full overflow-hidden rounded-full">
      <div className="absolute inset-0 bg-white/[0.06]" />
      <div
        className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out"
        style={{
          width: `${pct}%`,
          background: `linear-gradient(90deg, ${BRAND}80, ${BRAND})`,
        }}
      />
    </div>
  )
}

/* ── Main component ────────────────────────────────────────────────── */

const TEAM_MEMBER_ROLES = ['manager', 'support', 'promoter']

const getTrackFromStorage = (): { track: TutorialTrack; isTeamMember: boolean } | null => {
  const user = localStorage.getItem('dizeeUser')
  if (!user) return null
  try {
    const data = JSON.parse(user)
    const role = data?.professionRole
    if (!role) return null
    if (role === 'artist') return { track: 'artist', isTeamMember: false }
    if (TEAM_MEMBER_ROLES.includes(role)) return { track: 'team-member', isTeamMember: true }
    return null
  } catch {
    return null
  }
}

const onboardingKey = (track: TutorialTrack) => `dizee-onboarding-${track}`
const ONBOARDING_RESUME_KEY = 'dizee-onboarding-resume'

export default function OnboardingTutorial() {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [track, setTrack] = useState<TutorialTrack | null>(null)
  const [isTeamMemberUser, setIsTeamMemberUser] = useState(false)
  const [connectingGoogle, setConnectingGoogle] = useState(false)
  const [entering, setEntering] = useState(false)

  const steps: Step[] = useMemo(() => {
    if (!track) return []
    if (track === 'artist') return artistSteps
    if (track === 'team-member') return teamSteps
    return artistSteps
  }, [track])

  useEffect(() => {
    const info = getTrackFromStorage()
    if (!info) return
    setTrack(info.track)
    setIsTeamMemberUser(info.isTeamMember)

    let cancelled = false
    let timeoutId: number | null = null

    const clearTimer = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
        timeoutId = null
      }
    }

    const decideShow = (serverCompleted: boolean | null, seenInStorage: boolean) => {
      if (cancelled) return
      clearTimer()
      if (serverCompleted === true) {
        localStorage.setItem(onboardingKey(info.track), new Date().toISOString())
        return
      }
      if (serverCompleted === false) {
        const resumeStepRaw = sessionStorage.getItem(ONBOARDING_RESUME_KEY)
        if (resumeStepRaw !== null) {
          sessionStorage.removeItem(ONBOARDING_RESUME_KEY)
          const totalSteps = info.track === 'artist' ? artistSteps.length : teamSteps.length
          const nextStep = Math.max(0, Math.min(parseInt(resumeStepRaw, 10), totalSteps - 1))
          setStepIndex(nextStep)
          setIsOpen(true)
          setEntering(true)
          return
        }
        timeoutId = window.setTimeout(() => {
          timeoutId = null
          if (!cancelled) {
            setStepIndex(0)
            setIsOpen(true)
            setEntering(true)
          }
        }, 900)
        return
      }
      if (seenInStorage) return
      const resumeStepRaw = sessionStorage.getItem(ONBOARDING_RESUME_KEY)
      if (resumeStepRaw !== null) {
        sessionStorage.removeItem(ONBOARDING_RESUME_KEY)
        const totalSteps = info.track === 'artist' ? artistSteps.length : teamSteps.length
        const nextStep = Math.max(0, Math.min(parseInt(resumeStepRaw, 10), totalSteps - 1))
        setStepIndex(nextStep)
        setIsOpen(true)
        setEntering(true)
        return
      }
      timeoutId = window.setTimeout(() => {
        timeoutId = null
        if (!cancelled) {
          setStepIndex(0)
          setIsOpen(true)
          setEntering(true)
        }
      }, 900)
    }

    const seenInStorage = !!localStorage.getItem(onboardingKey(info.track))
    const typeParam = info.isTeamMember ? 'team' : 'artist'
    getDataAPI(`user/onboarding-completed?type=${typeParam}`)
      .then((res: any) => {
        const outer = res?.data ?? res
        const data = outer?.data ?? outer
        const completed = data?.completed === true
        decideShow(completed, seenInStorage)
      })
      .catch(() => {
        decideShow(null, seenInStorage)
      })

    return () => {
      cancelled = true
      clearTimer()
    }
  }, [])

  useEffect(() => {
    if (entering) {
      const id = window.setTimeout(() => setEntering(false), 50)
      return () => window.clearTimeout(id)
    }
  }, [entering])


  const tutorialFired = useRef(false)
  useEffect(() => {
    if (isOpen && !tutorialFired.current) {
      tutorialFired.current = true
      trackTutorialBegin()
      logger.info('Onboarding tutorial started', { flow: 'onboarding' })
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previousBodyOverflow }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') markComplete()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen])

  const markComplete = () => {
    if (!track) return
    const iso = new Date().toISOString()
    localStorage.setItem(onboardingKey(track), iso)
    setIsOpen(false)
    trackTutorialComplete(track)
    trackCoreActivationCompleted(track)
    logger.info('Onboarding tutorial completed', { flow: 'onboarding' })
    const typeParam = isTeamMemberUser ? 'team' : 'artist'
    postDataAPI('user/onboarding-completed', { completed: true, completedAt: iso, type: typeParam }).catch(() => {})
  }

  const handleNext = () => {
    if (stepIndex >= steps.length - 1) {
      markComplete()
      return
    }
    setStepIndex((c) => c + 1)
  }

  const handleBack = () => {
    setStepIndex((c) => Math.max(0, c - 1))
  }

  const handleAction = async (actionType: string) => {
    switch (actionType) {
      case 'openGoogleCalendar': {
        setConnectingGoogle(true)
        try {
          sessionStorage.setItem('googleConnecting', '1')
          sessionStorage.setItem(ONBOARDING_RESUME_KEY, String(stepIndex + 1))
          const redirectUri = `${window.location.origin}/google-calendar-callback`
          const resp = await getDataAPI(
            `user/connectors/googleCalendar/auth-url?redirectUri=${encodeURIComponent(redirectUri)}`
          )
          const authUrl =
            resp?.data?.data?.authUrl ?? resp?.data?.authUrl ?? resp?.authUrl ?? resp?.data?.data?.url ?? resp?.url
          if (!authUrl || typeof authUrl !== 'string') {
            sessionStorage.removeItem(ONBOARDING_RESUME_KEY)
            window.alert('Failed to initiate Google connection.')
            return
          }
          window.location.href = authUrl
        } catch {
          sessionStorage.removeItem(ONBOARDING_RESUME_KEY)
          window.alert('Failed to initiate Google connection. Please try again.')
        } finally {
          setConnectingGoogle(false)
        }
        return
      }
      case 'openGoogleSheets': {
        const sheetsTemplateId = import.meta.env.VITE_GOOGLE_SHEETS_TEMPLATE_ID || '1FoYR6rZzvxBXshWZXREB-1P1Mg9udLcGPFFJmzOACj8'
        window.open(`https://docs.google.com/spreadsheets/d/${sheetsTemplateId}/copy`, '_blank')
        markComplete()
        setTimeout(() => navigate('/settings'), 500)
        return
      }
      case 'openShows':
        markComplete()
        navigate('/shows')
        return
      case 'openMusic':
        markComplete()
        navigate('/music')
        return
      case 'openTeam':
        markComplete()
        navigate('/team')
        return
      case 'openSettings':
        markComplete()
        navigate('/settings?open=sheets')
        return
    }
  }

  if (!track || steps.length === 0 || !isOpen) return null

  const currentStep = steps[stepIndex]
  const VisualComponent = visualComponents[currentStep.visual]

  const overlay = (
    <div
      className="fixed inset-0 z-[120] flex flex-col select-none"
      style={{ backgroundColor: BG, transition: 'opacity 0.4s ease', opacity: entering ? 0 : 1 }}
      role="dialog"
      aria-modal="true"
    >
      {/* Background glows */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute w-[500px] h-[500px] rounded-full opacity-[0.04] blur-[120px]"
          style={{ background: `radial-gradient(circle, ${BRAND} 0%, transparent 70%)`, top: '-15%', right: '-8%' }}
        />
        <div
          className="absolute w-[400px] h-[400px] rounded-full opacity-[0.03] blur-[100px]"
          style={{ background: `radial-gradient(circle, ${BRAND} 0%, transparent 70%)`, bottom: '-15%', left: '-8%' }}
        />
      </div>

      {/* Fixed header: logo + progress bar + skip — pinned top */}
      <div className="relative z-10 w-full shrink-0 px-4 sm:px-8 pt-[max(1rem,env(safe-area-inset-top))]">
        <div className="mx-auto flex w-full max-w-[880px] items-center gap-5 py-4">
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="w-4 h-4 rounded-sm" style={{ background: BRAND }} />
            <BrandLogo className="h-2.5 w-auto text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <ProgressBar current={stepIndex} total={steps.length} />
          </div>
          <button
            type="button"
            onClick={markComplete}
            className="shrink-0 text-[11px] text-zinc-500 hover:text-white transition-colors font-medium tracking-wide uppercase"
          >
            Skip
          </button>
        </div>
      </div>

      {/* Scrollable content area — stable height, no jump */}
      <div className="relative z-10 flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-hide px-4 sm:px-8">
          <div
            key={stepIndex}
            className="mx-auto w-full max-w-[880px] tut-slide-enter"
          >
            {/* Two-column layout: text + visual */}
            <div className="flex flex-col lg:flex-row gap-5 sm:gap-8 py-4 sm:py-6">
              {/* Left: text content */}
              <div className="flex-1 min-w-0 flex flex-col">
                {/* Tag */}
                <span className="inline-block text-[11px] font-semibold uppercase tracking-widest" style={{ color: BRAND }}>
                  {currentStep.tag}
                </span>

                {/* Title */}
                <h1 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-white leading-[1.12]">
                  {currentStep.title}
                </h1>

                {/* Subtitle */}
                <p className="mt-3 text-[13px] sm:text-sm text-zinc-400 leading-relaxed max-w-[420px]">
                  {currentStep.subtitle}
                </p>

                {/* Divider */}
                <div className="mt-5 h-[1px] w-12 bg-white/[0.08]" />

                {/* Highlight cards */}
                <div className="mt-5 space-y-2.5 flex-1">
                  {currentStep.highlights.map((h) => (
                    <div key={h.label} className="group flex gap-3.5 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3.5 sm:p-4 transition-colors hover:border-white/[0.1] hover:bg-white/[0.05]">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.06]">
                        <h.icon className="h-4 w-4 text-zinc-400 group-hover:text-white transition-colors" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[13px] font-medium text-white">{h.label}</p>
                        <p className="mt-1 text-[11px] sm:text-[12px] text-zinc-500 leading-relaxed">{h.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action buttons — inline with text content */}
                {currentStep.actions ? (
                  <div className="mt-5 flex flex-wrap gap-3">
                    {currentStep.actions.map((action) => {
                      const isGoogleCalendar = action.onClick === 'openGoogleCalendar'
                      const isLoading = isGoogleCalendar && connectingGoogle
                      return (
                        <button
                          key={action.label}
                          type="button"
                          onClick={() => handleAction(action.onClick)}
                          disabled={isLoading}
                          className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white transition-all disabled:opacity-60 disabled:cursor-wait"
                          style={{
                            background: BRAND,
                            boxShadow: '0 0 24px rgba(255,35,0,0.2)',
                          }}
                        >
                          {isLoading ? 'Connecting...' : action.label}
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      )
                    })}
                  </div>
                ) : null}
              </div>

              {/* Right: visual mockup */}
              <div className="w-full lg:w-[280px] shrink-0 lg:pt-6">
                <VisualComponent />
              </div>
            </div>
          </div>
        </div>

        {/* Fixed footer nav — pinned bottom */}
        <div className="shrink-0 px-4 sm:px-8 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="mx-auto flex w-full max-w-[880px] items-center justify-between border-t border-white/[0.06] pt-4 pb-1">
            <div className="w-16">
              {stepIndex > 0 ? (
                <button
                  type="button"
                  onClick={handleBack}
                  className="text-[12px] text-zinc-500 hover:text-white transition-colors font-medium"
                >
                  Back
                </button>
              ) : null}
            </div>

            {/* Dot indicators */}
            <div className="flex items-center gap-1.5">
              {steps.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setStepIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === stepIndex ? 'w-6' : 'w-1.5 hover:opacity-80'
                  }`}
                  style={{
                    background: i === stepIndex ? BRAND : 'rgba(255,255,255,0.15)',
                  }}
                  aria-label={`Step ${i + 1}`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={handleNext}
              className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-[12px] font-semibold text-white transition-all"
              style={{
                background: stepIndex >= steps.length - 1 ? BRAND : 'rgba(255,255,255,0.08)',
                boxShadow: stepIndex >= steps.length - 1 ? '0 0 20px rgba(255,35,0,0.15)' : 'none',
              }}
            >
              {stepIndex >= steps.length - 1 ? 'Get Started' : 'Continue'}
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        @keyframes tutSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .tut-slide-enter {
          animation: tutSlideIn 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
        }
      `}</style>
    </div>
  )

  return typeof document === 'undefined' ? null : createPortal(overlay, document.body)
}
